import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Stack, Title, Text, Group, Badge, Image, Card, Button, SimpleGrid, Skeleton, Divider, Paper } from '@mantine/core';
import { IconMapPin, IconPhone, IconMail, IconWorld, IconTent, IconArrowLeft, IconExternalLink } from '@tabler/icons-react';

const CampgroundDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campground, setCampground] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCampground();
  }, [id]);

  const fetchCampground = async () => {
    try {
      const response = await fetch(`/api/campgrounds/${id}`);
      const data = await response.json();
      
      if (data.success) {
        setCampground(data.data);
      } else {
        console.error('Error fetching campground:', data.message);
      }
    } catch (error) {
      console.error('Error fetching campground:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAmenityCategoryColor = (category) => {
    switch (category) {
      case 'RV Type': return 'blue';
      case 'Park Amenity': return 'green';
      case 'Utility': return 'orange';
      default: return 'gray';
    }
  };

  const groupedAmenities = campground?.amenities?.reduce((acc, amenity) => {
    if (!acc[amenity.category]) {
      acc[amenity.category] = [];
    }
    acc[amenity.category].push(amenity);
    return acc;
  }, {}) || {};

  const getGoogleMapsUrl = (address, lat, lng) => {
    // Always include the full address for better accuracy
    // Zoom level 14 provides a good view with the marker centered and visible
    if (address) {
      const encodedAddress = encodeURIComponent(address);
      if (lat && lng) {
        // Include both address and coordinates, center on coordinates
        return `https://www.google.com/maps?q=${encodedAddress}&center=${lat},${lng}&zoom=14&output=embed`;
      }
      return `https://www.google.com/maps?q=${encodedAddress}&zoom=14&output=embed`;
    }
    // Fallback to coordinates only if no address
    if (lat && lng) {
      return `https://www.google.com/maps?q=${lat},${lng}&zoom=14&output=embed`;
    }
    return null;
  };

  const getGoogleMapsLink = (address, lat, lng) => {
    // Always include the full address for better accuracy
    if (address) {
      const encodedAddress = encodeURIComponent(address);
      if (lat && lng) {
        // Include both address and coordinates for best accuracy
        return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}&ll=${lat},${lng}`;
      }
      return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    }
    // Fallback to coordinates only if no address
    if (lat && lng) {
      return `https://www.google.com/maps?q=${lat},${lng}`;
    }
    return null;
  };

  if (loading) {
    return (
      <Container size="xl" py="md">
        <Stack gap="xl">
          <Skeleton height={400} />
          <Skeleton height={100} />
          <Skeleton height={200} />
        </Stack>
      </Container>
    );
  }

  if (!campground) {
    return (
      <Container size="xl" py="md">
        <Stack gap="md" align="center">
          <Title order={2}>Campground Not Found</Title>
          <Text c="dimmed">The campground you're looking for doesn't exist.</Text>
          <Button component={Link} to="/campgrounds" leftSection={<IconArrowLeft size={16} />}>
            Back to Campgrounds
          </Button>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="xl">
        {/* Back Button */}
        <Button
          variant="light"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>

        {/* Header Section */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="flex-start">
              <Stack gap="xs">
                <Group gap="md">
                  <Title order={1}>{campground.name}</Title>
                  {campground.featured && (
                    <Badge color="green" size="lg">Featured</Badge>
                  )}
                  <Badge color="orange" size="lg">{campground.type}</Badge>
                </Group>
                <Group gap="xs" c="dimmed">
                  <IconMapPin size={18} />
                  <Text size="lg">
                    {campground.address}, {campground.city}, {campground.state} {campground.zipCode}
                  </Text>
                </Group>
              </Stack>
            </Group>

            {/* Thumbnail Image */}
            {campground.thumbnail && (
              <Image
                src={campground.thumbnail}
                height={400}
                radius="md"
                alt={campground.name}
                style={{ objectFit: 'cover' }}
              />
            )}
          </Stack>
        </Card>

        {/* Map Section */}
        {(campground.latitude && campground.longitude) || campground.address ? (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <Title order={3}>Location</Title>
                {getGoogleMapsLink(
                  `${campground.address}, ${campground.city}, ${campground.state} ${campground.zipCode}`,
                  campground.latitude,
                  campground.longitude
                ) && (
                  <Button
                    component="a"
                    href={getGoogleMapsLink(
                      `${campground.address}, ${campground.city}, ${campground.state} ${campground.zipCode}`,
                      campground.latitude,
                      campground.longitude
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="light"
                    color="orange"
                    rightSection={<IconExternalLink size={16} />}
                  >
                    Open in Google Maps
                  </Button>
                )}
              </Group>
              {getGoogleMapsUrl(
                `${campground.address}, ${campground.city}, ${campground.state} ${campground.zipCode}`,
                campground.latitude,
                campground.longitude
              ) ? (
                <div style={{ width: '100%', height: '400px', borderRadius: '8px', overflow: 'hidden' }}>
                  <iframe
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    src={getGoogleMapsUrl(
                      `${campground.address}, ${campground.city}, ${campground.state} ${campground.zipCode}`,
                      campground.latitude,
                      campground.longitude
                    )}
                    title="Campground Location Map"
                  />
                </div>
              ) : (
                <Paper p="xl" radius="md" withBorder>
                  <Stack align="center" gap="md">
                    <IconMapPin size={48} color="dimmed" />
                    <Text c="dimmed" ta="center">
                      Map unavailable. Address: {campground.address}, {campground.city}, {campground.state}
                    </Text>
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Card>
        ) : null}

        {/* Contact Information */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={3} mb="md">Contact Information</Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {campground.mainPhone && (
              <Group gap="xs">
                <IconPhone size={20} color="orange" />
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">Main Phone</Text>
                  <Text fw={500}>{campground.mainPhone}</Text>
                </Stack>
              </Group>
            )}
            {campground.reservationPhone && campground.reservationPhone !== campground.mainPhone && (
              <Group gap="xs">
                <IconPhone size={20} color="orange" />
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">Reservation Phone</Text>
                  <Text fw={500}>{campground.reservationPhone}</Text>
                </Stack>
              </Group>
            )}
            {campground.email && (
              <Group gap="xs">
                <IconMail size={20} color="orange" />
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">Email</Text>
                  <Text fw={500}>{campground.email}</Text>
                </Stack>
              </Group>
            )}
            {campground.website && (
              <Group gap="xs">
                <IconWorld size={20} color="orange" />
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">Website</Text>
                  <Button
                    component="a"
                    href={campground.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="light"
                    size="sm"
                    rightSection={<IconExternalLink size={14} />}
                  >
                    Visit Website
                  </Button>
                </Stack>
              </Group>
            )}
            {campground.url && (
              <Group gap="xs">
                <IconExternalLink size={20} color="orange" />
                <Stack gap={0}>
                  <Text size="xs" c="dimmed">Reservation Link</Text>
                  <Button
                    component="a"
                    href={campground.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="light"
                    size="sm"
                    color="orange"
                    rightSection={<IconExternalLink size={14} />}
                  >
                    Book Now
                  </Button>
                </Stack>
              </Group>
            )}
          </SimpleGrid>
        </Card>

        {/* Description */}
        {campground.description && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Title order={3} mb="md">About</Title>
            <Text style={{ whiteSpace: 'pre-line' }}>{campground.description}</Text>
          </Card>
        )}

        {/* Amenities */}
        {campground.amenities && campground.amenities.length > 0 && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Title order={3} mb="md">Amenities</Title>
            <Stack gap="lg">
              {Object.entries(groupedAmenities).map(([category, amenities]) => (
                <Stack key={category} gap="xs">
                  <Text fw={600} size="lg" c="dimmed">{category}</Text>
                  <Group gap="xs" wrap="wrap">
                    {amenities.map((amenity) => (
                      <Badge
                        key={amenity.id}
                        size="lg"
                        color={getAmenityCategoryColor(category)}
                        variant="light"
                      >
                        {amenity.name}
                      </Badge>
                    ))}
                  </Group>
                  <Divider />
                </Stack>
              ))}
            </Stack>
          </Card>
        )}

        {/* Location Details */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={3} mb="md">Location Details</Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" c="dimmed">Address</Text>
              <Text fw={500}>{campground.address}</Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">City</Text>
              <Text fw={500}>{campground.city}</Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">State</Text>
              <Text fw={500}>{campground.state}</Text>
            </Stack>
            {campground.zipCode && (
              <Stack gap="xs">
                <Text size="sm" c="dimmed">Zip Code</Text>
                <Text fw={500}>{campground.zipCode}</Text>
              </Stack>
            )}
            {campground.latitude && campground.longitude && (
              <>
                <Stack gap="xs">
                  <Text size="sm" c="dimmed">Latitude</Text>
                  <Text fw={500}>{campground.latitude}</Text>
                </Stack>
                <Stack gap="xs">
                  <Text size="sm" c="dimmed">Longitude</Text>
                  <Text fw={500}>{campground.longitude}</Text>
                </Stack>
              </>
            )}
          </SimpleGrid>
        </Card>

        {/* Call to Action */}
        <Paper p="lg" radius="md" withBorder bg="orange.0">
          <Group justify="space-between" align="center">
            <Stack gap="xs">
              <Title order={4}>Ready to Visit?</Title>
              <Text c="dimmed">Get in touch or visit their website to make a reservation</Text>
            </Stack>
            <Group>
              {campground.url && (
                <Button
                  component="a"
                  href={campground.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="orange"
                  size="lg"
                  rightSection={<IconExternalLink size={16} />}
                >
                  Book Now
                </Button>
              )}
              {campground.website && !campground.url && (
                <Button
                  component="a"
                  href={campground.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="orange"
                  size="lg"
                  rightSection={<IconExternalLink size={16} />}
                >
                  Visit Website
                </Button>
              )}
              {campground.mainPhone && (
                <Button
                  component="a"
                  href={`tel:${campground.mainPhone.replace(/\D/g, '')}`}
                  variant="outline"
                  color="orange"
                  size="lg"
                  leftSection={<IconPhone size={16} />}
                >
                  Call Now
                </Button>
              )}
            </Group>
          </Group>
        </Paper>
      </Stack>
    </Container>
  );
};

export default CampgroundDetail;

